// TV-news-style 5-day weather widget. Renders a current-temperature hero
// pane on the left and five day cards below for the upcoming forecast.
//
// The widget is rendered fullscreen by PlayerStage when a manifest playlist
// item has kind='weather'. It fetches forecast data via src/lib/weather.ts
// and falls back gracefully if the network is unavailable on the kiosk.

import { useEffect, useState } from 'react';
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Sun,
} from 'lucide-react';
import {
  describeWeatherCode,
  getWeatherSnapshot,
  type WeatherIconKind,
  type WeatherSnapshot,
} from '@/lib/weather';

interface Props {
  zipCode: string;
}

const ICON_BY_KIND: Record<WeatherIconKind, typeof Sun> = {
  sun: Sun,
  'cloud-sun': CloudSun,
  cloud: Cloud,
  'cloud-fog': CloudFog,
  'cloud-drizzle': CloudDrizzle,
  'cloud-rain': CloudRain,
  'cloud-snow': CloudSnow,
  'cloud-lightning': CloudLightning,
};

function dayLabel(iso: string, index: number): string {
  if (index === 0) return 'TODAY';
  // iso is YYYY-MM-DD; build a date in the local TZ at noon to dodge DST edge.
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0);
  return date
    .toLocaleDateString(undefined, { weekday: 'short' })
    .toUpperCase();
}

function formatLongDate(now: Date): string {
  return now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function WeatherWidget({ zipCode }: Props) {
  const [snapshot, setSnapshot] = useState<WeatherSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setSnapshot(null);
    getWeatherSnapshot(zipCode)
      .then((snap) => {
        if (!cancelled) setSnapshot(snap);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Could not load forecast';
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [zipCode]);

  // Refresh the wall clock once a minute so the date stays accurate on
  // long-running kiosks.
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  if (error && !snapshot) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-black p-12 text-center text-white">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-[0.3em] text-white/40">Weather Unavailable</div>
          <div className="text-lg text-white/70">ZIP {zipCode}</div>
          <div className="text-sm text-white/50">{error}</div>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-black text-sm text-white/60">
        Loading forecast…
      </div>
    );
  }

  const current = describeWeatherCode(snapshot.currentWeatherCode);
  const CurrentIcon = ICON_BY_KIND[current.icon] ?? Cloud;

  return (
    <div className="absolute inset-0 flex flex-col bg-gradient-to-br from-sky-900 via-slate-900 to-slate-950 text-white">
      {/* Header bar */}
      <header className="flex items-center justify-between border-b border-white/10 px-[5%] py-[2.5%]">
        <div className="flex items-baseline gap-[2.5vw]">
          <span className="text-[3.2vh] font-black uppercase tracking-[0.18em] text-amber-300 drop-shadow">
            5-Day Forecast
          </span>
          <span className="text-[2.4vh] font-medium uppercase tracking-[0.25em] text-white/70">
            {snapshot.location}
          </span>
        </div>
        <div className="text-[2vh] uppercase tracking-[0.18em] text-white/50">
          {formatLongDate(now)}
        </div>
      </header>

      {/* Hero current temperature */}
      <section className="flex flex-1 items-center justify-between px-[5%]">
        <div className="flex items-center gap-[3vw]">
          <CurrentIcon
            strokeWidth={1.4}
            className="h-[28vh] w-[28vh] text-amber-200 drop-shadow-[0_0_18px_rgba(255,200,80,0.35)]"
          />
          <div>
            <div className="text-[2.2vh] uppercase tracking-[0.25em] text-white/60">
              Currently
            </div>
            <div className="flex items-start">
              <span className="text-[20vh] font-black leading-none drop-shadow">
                {snapshot.currentF}
              </span>
              <span className="mt-[1vh] text-[6vh] font-light leading-none text-white/80">
                °F
              </span>
            </div>
            <div className="text-[3vh] font-medium tracking-wide text-white/85">
              {current.label}
            </div>
          </div>
        </div>

        <div className="hidden flex-col items-end gap-[1vh] text-right md:flex">
          <div className="text-[2vh] uppercase tracking-[0.25em] text-white/60">
            ZIP
          </div>
          <div className="text-[5vh] font-semibold tracking-[0.05em]">
            {snapshot.zipCode}
          </div>
        </div>
      </section>

      {/* 5-day strip */}
      <section className="grid grid-cols-5 gap-[1vw] px-[5%] pb-[3.5%]">
        {snapshot.days.slice(0, 5).map((day, idx) => {
          const meta = describeWeatherCode(day.weatherCode);
          const DayIcon = ICON_BY_KIND[meta.icon] ?? Cloud;
          return (
            <div
              key={day.date}
              className={`flex flex-col items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-[1vw] py-[2.5vh] backdrop-blur-sm ${
                idx === 0 ? 'ring-2 ring-amber-300/70' : ''
              }`}
            >
              <div className="text-[2.4vh] font-bold uppercase tracking-[0.2em] text-amber-200">
                {dayLabel(day.date, idx)}
              </div>
              <DayIcon
                strokeWidth={1.4}
                className="my-[1.5vh] h-[8vh] w-[8vh] text-white/90"
              />
              <div className="text-[1.6vh] uppercase tracking-[0.18em] text-white/55">
                {meta.label}
              </div>
              <div className="mt-[1.5vh] flex items-baseline gap-[1vw]">
                <span className="text-[5vh] font-black leading-none">{day.highF}°</span>
                <span className="text-[3vh] font-light leading-none text-white/55">
                  {day.lowF}°
                </span>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
